"use client";

import { GlassCard, IconBadge, StaggerGroup, StaggerItem } from "../shared";
import { RocketIcon, SearchIcon, ChevronRightIcon } from "../shared/icons";

type FeatureCard = {
  href: string;
  icon: typeof RocketIcon;
  glow: "cyan" | "purple";
  /** Tailwind `bg-icon-grad-*` utility (tailwind.config.js) matching this card's swatch. */
  gradient: string;
  title: string;
  tagline: string;
  /** Short verb phrase shown under the tagline instead of a bare chevron. */
  action: string;
};

const CARDS: FeatureCard[] = [
  {
    href: "/login",
    icon: RocketIcon,
    glow: "purple",
    gradient: "bg-icon-grad-purple",
    title: "Claim Your Profile",
    tagline: "Build trust. Get verified. Be discovered.",
    action: "Get verified",
  },
  {
    href: "/search",
    icon: SearchIcon,
    glow: "cyan",
    gradient: "bg-icon-grad-blue",
    title: "Explore Wallets",
    tagline: "Analyze wallets & verify activity.",
    action: "Start exploring",
  },
];

/**
 * Two primary entry-point cards beneath the search bar: "Claim Your Profile"
 * and "Explore Wallets". Matches the client reference exactly - icon, title,
 * one-line tagline, trailing chevron, no eyebrow/bullets/extra CTA pill.
 */
export default function FeatureActionCards() {
  return (
    <section aria-label="Get started with Reqrium" className="page-section relative w-full py-6 sm:py-8">
      <StaggerGroup className="mx-auto max-w-6xl grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        {CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <StaggerItem key={card.href}>
              <GlassCard href={card.href} glow={card.glow} className="flex items-center gap-4 p-6 sm:p-7">
                <IconBadge variant="gradient" accent={card.gradient} size="md">
                  <Icon size={22} />
                </IconBadge>
                <span className="min-w-0 flex-1">
                  <span className="block text-lg font-bold leading-tight" style={{ color: "#f1f5f9" }}>
                    {card.title}
                  </span>
                  <span className="mt-1 block text-sm leading-snug" style={{ color: "var(--muted-text, #94a3b8)" }}>
                    {card.tagline}
                  </span>
                  {/* A named action reads as a deliberate next step rather
                      than a bare chevron the visitor has to interpret. */}
                  <span className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-200/85 transition-colors group-hover/glass:text-cyan-100">
                    {card.action}
                  </span>
                </span>
                <ChevronRightIcon
                  size={20}
                  className="shrink-0 transition-transform duration-300 group-hover/glass:translate-x-1"
                  style={{ color: "var(--muted-text, #64748b)" }}
                />
              </GlassCard>
            </StaggerItem>
          );
        })}
      </StaggerGroup>
    </section>
  );
}
